<template>
  <div class="flex flex-col min-h-screen bg-gray-100">
    <!-- Invite Experts Modal -->
    <div
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div
        :class="[
          'bg-white rounded-lg shadow-lg w-full h-full max-w-full max-h-full relative flex flex-col',
          selectedTab === 'manual' || selectedTab === 'bulk'
            ? 'overflow-auto'
            : 'overflow-hidden',
        ]"
      >
        <button
          class="absolute top-4 right-6 text-2xl text-gray-400 hover:text-gray-700 z-10"
          @click="$emit('close')"
        >
          &times;
        </button>
        <main class="flex flex-col w-full h-full justify-start pt-12">
          <div
            class="flex flex-col items-start w-full gap-3 px-6 sm:px-24 text-left"
          >
            <div class="flex flex-col mb-1">
              <span
                class="text-3xl leading-9 font-semibold text-neutral-800 font-inter"
                >Invite Experts</span
              >
              <span
                v-if="!onboardingCompleted"
                class="text-sm leading-5 font-medium text-neutral-800"
                >Almost done! Invite experts to the program</span
              >
            </div>
            <div class="flex justify-center mb-4">
              <div
                v-if="errorMessage"
                role="alert"
                class="flex gap-2 w-full justify-center p-2.5 mt-6 text-sm leading-5 text-center text-red-900 bg-red-50 rounded-lg max-md:flex-wrap"
                style="max-height: 80px; overflow-y: auto"
              >
                <div
                  class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full"
                >
                  <img
                    src="../../assets/shared/failure.svg"
                    alt="Error Icon"
                    class="w-4 h-4"
                  />
                </div>
                <p>{{ errorMessage }}</p>
              </div>
            </div>
            <InviteTabs
              :selectedTab="selectedTab"
              @tab-change="selectedTab = $event"
            />
            <template v-if="selectedTab === 'manual'">
              <InviteByEmailSection
                :key="'manual'"
                invitationType="experts"
                :directSignup="showLandingPage"
                @go-back-to-experts="handleGoBackToExperts"
              >
                <template #checkbox>
                  <div class="flex items-center mt-2 mb-2">
                    <BaseCheckbox
                      v-model="showLandingPage"
                      id="show-landing-page-checkbox"
                      label="Direct users to signup form without landing page"
                    />
                  </div>
                </template>
              </InviteByEmailSection>
              <LandingPagePreviewSection
                :previewImg="invitePageImg"
                :title="programName"
                subtitle="The experts will land on this landing page when they click to join the program"
                about="Welcome to the Expert Program. Our Expert Program is a structured initiative designed to identify, engage, and leverage the knowledge and expertise of our customers, partners, and advocates. Experts will have the opportunity to meet with prospective buyers or customers and share your product and industry insights and experience. We're excited to have you join our Expert Program!"
                :programUuid="programUuid"
                @preview="() => {}"
              />
            </template>
            <template v-if="selectedTab === 'bulk'">
              <InviteBulkSection
                v-if="selectedTab === 'bulk'"
                :key="'bulk'"
                :programId="programId"
                :companyId="companyId"
                invitationType="experts"
                :directSignup="showLandingPageBulk"
                @invites-sent="handleBulkInvitesSent"
              >
                <template #checkbox>
                  <div class="flex items-center mt-2 mb-2">
                    <BaseCheckbox
                      v-model="showLandingPageBulk"
                      id="show-landing-page-checkbox-bulk"
                      label="Direct users to signup form without landing page"
                    />
                  </div>
                </template>
              </InviteBulkSection>
              <LandingPagePreviewSection
                :previewImg="invitePageImg"
                :title="programName"
                subtitle="The experts will land on this landing page when they click to join the program"
                about="Welcome to the Expert Program. Our Expert Program is a structured initiative designed to identify, engage, and leverage the knowledge and expertise of our customers, partners, and advocates. Experts will have the opportunity to meet with prospective buyers or customers and share your product and industry insights and experience. We're excited to have you join our Expert Program!"
                :programUuid="programUuid"
                @preview="() => {}"
              />
            </template>
            <template v-if="selectedTab === 'link'">
              <InviteByLinkSection
                invitationType="experts"
                :directSignup="showLandingPageLink"
              />
              <div class="mt-4">
                <BaseCheckbox
                  v-model="showLandingPageLink"
                  id="show-landing-page-checkbox-link"
                  label="Direct users to signup form without landing page"
                />
              </div>
              <LandingPagePreviewSection
                :previewImg="invitePageImg"
                :title="programName"
                subtitle="The experts will land on this landing page when they click to join the program"
                about="Welcome to the Expert Program. Our Expert Program is a structured initiative designed to identify, engage, and leverage the knowledge and expertise of our customers, partners, and advocates. Experts will have the opportunity to meet with prospective buyers or customers and share your product and industry insights and experience. We're excited to have you join our Expert Program!"
                :programUuid="programUuid"
                @preview="() => {}"
              />
            </template>
            <template v-if="selectedTab === 'software'">
              <InviteViaSoftwareSection
                :softwareList="softwareList"
                :directSignup="showLandingPageSoftware"
                @software-click="handleSoftwareClick"
              />
              <div class="mt-4">
                <BaseCheckbox
                  v-model="showLandingPageSoftware"
                  id="show-landing-page-checkbox-software"
                  label="Direct users to signup form without landing page"
                />
              </div>
              <LandingPagePreviewSection
                :previewImg="invitePageImg"
                :title="programName"
                subtitle="The experts will land on this landing page when they click to join the program"
                about="Welcome to the Expert Program. Our Expert Program is a structured initiative designed to identify, engage, and leverage the knowledge and expertise of our customers, partners, and advocates. Experts will have the opportunity to meet with prospective buyers or customers and share your product and industry insights and experience. We're excited to have you join our Expert Program!"
                :programUuid="programUuid"
                @preview="() => {}"
              />
            </template>
          </div>
          <footer class="flex flex-wrap justify-center mt-auto items-center">
            <FooterSection
              v-if="!onboardingCompleted"
              :isDisabled="isDisabled"
              :setErrorMessage="setErrorMessage"
              currentView="invite-experts"
            />
          </footer>
        </main>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import InviteTabs from "@/components/expert-program/invite-experts/InviteTabs.vue";
import InviteByEmailSection from "@/components/expert-program/invite-experts/InviteByEmailSection.vue";
import InviteByLinkSection from "@/components/expert-program/invite-experts/InviteByLinkSection.vue";
import LandingPagePreviewSection from "@/components/expert-program/invite-experts/LandingPagePreviewSection.vue";
import InviteViaSoftwareSection from "@/components/expert-program/invite-experts/InviteViaSoftwareSection.vue";
import InviteBulkSection from "@/components/expert-program/invite-experts/InviteBulkSection.vue";
import FooterSection from "@/components/expert-program/FooterSection.vue";
import invitePageImg from "@/assets/shared/invite_page.svg";
import marketoIcon from "@/assets/shared/marketo.png";
import hubspotIcon from "@/assets/shared/hubspot.svg";
import activeCampaignIcon from "@/assets/shared/active_campaign.png";
import inflectionIcon from "@/assets/shared/inflection.png";
import salesforceIcon from "@/assets/shared/salesforce-logo.svg";
import eloquaIcon from "@/assets/shared/eloqua.png";
import oktaIcon from "@/assets/shared/okta.png";
import mailchimpIcon from "@/assets/shared/mailchimp.png";
import iterableIcon from "@/assets/shared/iterable.png";
import gainsightIcon from "@/assets/shared/gainsight.png";
import churnzeroIcon from "@/assets/shared/churnzero.png";
import vitallyIcon from "@/assets/shared/vitally.png";
import BaseCheckbox from "@/components/shared/BaseCheckbox.vue";

export default defineComponent({
  name: "InviteExpertsView",
  components: {
    InviteTabs,
    InviteByEmailSection,
    InviteBulkSection,
    InviteByLinkSection,
    LandingPagePreviewSection,
    InviteViaSoftwareSection,
    FooterSection,
    BaseCheckbox,
  },
  setup() {
    const router = useRouter();
    const selectedTab = ref<"manual" | "bulk" | "link" | "software">("manual");
    const errorMessage = ref("");
    const isDisabled = ref(false);
    const softwareList = ref([
      {
        id: "marketo",
        name: "Marketo",
        icon: marketoIcon,
        url: "https://login.marketo.com/",
      },
      {
        id: "hubspot",
        name: "HubSpot",
        icon: hubspotIcon,
        url: "https://app.hubspot.com/login",
      },
      {
        id: "activecampaign",
        name: "ActiveCampaign",
        icon: activeCampaignIcon,
        url: "https://www.activecampaign.com/login",
      },
      {
        id: "inflection",
        name: "Inflection",
        icon: inflectionIcon,
        url: "https://app.inflection.io/login/start",
      },
      {
        id: "sfmc",
        name: "Salesforce Marketing Cloud",
        icon: salesforceIcon,
        url: "https://login.salesforce.com/",
      },
      {
        id: "eloqua",
        name: "Eloqua",
        icon: eloquaIcon,
        url: "https://login.eloqua.com/",
      },
      {
        id: "okta",
        name: "Okta",
        icon: oktaIcon,
        url: "https://login.okta.com/",
      },
      {
        id: "mailchimp",
        name: "Mailchimp",
        icon: mailchimpIcon,
        url: "https://login.mailchimp.com/",
      },
      {
        id: "iterable",
        name: "Iterable",
        icon: iterableIcon,
        url: "https://app.iterable.com/",
      },
      {
        id: "gainsight",
        name: "Gainsight",
        icon: gainsightIcon,
        url: "https://auth.gainsightcloud.com/",
      },
      {
        id: "churnzero",
        name: "ChurnZero",
        icon: churnzeroIcon,
        url: "https://app.churnzero.net/Login",
      },
      {
        id: "vitally",
        name: "Vitally",
        icon: vitallyIcon,
        url: "https://login.vitally.io/",
      },
    ]);
    const userStore = useUserStore();
    const programUuid = computed(() => {
      return (
        userStore.createExpertProgram?.uuid ||
        userStore.selectedProgram?.uuid ||
        ""
      );
    });
    const programId = computed(() => {
      return (
        userStore.createExpertProgram?.id || userStore.selectedProgram?.id || ""
      );
    });
    const programName = computed(() => {
      const name =
        userStore.createExpertProgram?.name || userStore.selectedProgram?.name;
      return name ? `${name} Program Landing Page` : "Program Landing Page";
    });
    const companyId = computed(() => {
      return userStore.companyId || "";
    });

    const setErrorMessage = (msg: string) => {
      errorMessage.value = msg;
    };

    const handleSoftwareClick = (software: {
      id: string;
      name: string;
      icon: string;
      url: string;
    }) => {
      window.open(software.url, "_blank");
    };

    const handleBulkInvitesSent = (payload: {
      success: boolean;
      message: string;
      emailCount: number;
    }) => {
      if (payload.success) {
        // Clear any existing error messages
        errorMessage.value = "";

        // Give users time to read the success message before refreshing
        setTimeout(() => {
          router.go(0); // This refreshes the current route
        }, 2500); // 2.5 seconds delay
      }
    };

    const onboardingCompleted = computed(() => {
      return userStore.createExpertProgram === null;
    });

    const showLandingPage = ref(true);
    const showLandingPageBulk = ref(true);
    const showLandingPageLink = ref(true);
    const showLandingPageSoftware = ref(true);

    // Handler for go-back-to-experts event
    const handleGoBackToExperts = () => {
      window.location.reload();
    };

    return {
      selectedTab,
      errorMessage,
      isDisabled,
      softwareList,
      setErrorMessage,
      invitePageImg,
      programUuid,
      handleSoftwareClick,
      handleBulkInvitesSent,
      companyId,
      onboardingCompleted,
      programId,
      handleGoBackToExperts,
      showLandingPage,
      showLandingPageBulk,
      showLandingPageLink,
      showLandingPageSoftware,
      programName,
    };
  },
});
</script>
